import { Cancel, CheckCircle } from "@mui/icons-material";
import { Box, Grid, Grow, styled, Typography } from "@mui/material";
import {
	calculateCharSets,
	containsLowerCase,
	containsNumber,
	containsSymbol,
	containsThreeCharacterTypes,
	containsUpperCase,
	isAcceptableLength,
	passwordsMatch,
} from "helpers/authValidation";

const PREFIX = "PasswordValidation";

const classes = {
	success: `${PREFIX}-success`,
	error: `${PREFIX}-error`,
	passSuccess: `${PREFIX}-passSuccess`,
};

const Root = styled("div")(({ theme }) => ({
	[`& .${classes.success}`]: {
		color: theme.palette.success.main,
		marginRight: theme.spacing(1),
	},

	[`& .${classes.error}`]: {
		color: theme.palette.error.main,
		marginRight: theme.spacing(1),
	},

	[`& .${classes.passSuccess}`]: {
		padding: theme.spacing(2),
		backgroundColor: theme.palette.success.main,
	},
}));

export interface PasswordValidationProps {
	password: string;
	confirmPassword: string;
}

interface CriteriaStatusProps {
	helper: (password: string) => boolean;
	title: string;
	password: string;
}
const CriteriaStatus = (props: CriteriaStatusProps): JSX.Element => {
	const { helper, title, password } = props;

	return (
		<Grid container>
			{helper(password) ? (
				<CheckCircle className={classes.success} />
			) : (
				<Cancel className={classes.error} />
			)}
			<Typography variant="subtitle1">{title}</Typography>
		</Grid>
	);
};

export const PasswordValidation = (
	props: PasswordValidationProps,
): JSX.Element => {
	const { password, confirmPassword } = props;

	const Success = (
		<Root>
			<Grow in timeout={1000}>
				<Box display="flex" marginTop={3} className={classes.passSuccess}>
					<Typography variant="subtitle2" style={{ color: "white" }}>
						🎉Your password is secure and you are all set!
					</Typography>
				</Box>
			</Grow>
		</Root>
	);

	return passwordsMatch(password, confirmPassword) ? (
		Success
	) : (
		<Root>
			<Box display="flex" paddingTop={3}>
				<Grid
					container
					direction="column"
					justifyContent="space-between"
					alignItems="flex-start"
				>
					<CriteriaStatus
						password={password}
						title={"a lower case"}
						helper={containsLowerCase}
					/>
					<CriteriaStatus
						password={password}
						title={"an upper case"}
						helper={containsUpperCase}
					/>
					<CriteriaStatus
						password={password}
						title={"a number"}
						helper={containsNumber}
					/>
				</Grid>
				<Grid container direction="column" justifyContent="space-between">
					<Grid
						container
						direction="column"
						justifyContent="flex-start"
						alignItems="flex-start"
					>
						<CriteriaStatus
							password={password}
							title={"a symbol"}
							helper={containsSymbol}
						/>
						<CriteriaStatus
							password={password}
							title={"at least 7 characters"}
							helper={isAcceptableLength}
						/>
						<Grid container>
							{passwordsMatch(password, confirmPassword) ? (
								<CheckCircle className={classes.success} />
							) : (
								<Cancel className={classes.error} />
							)}
							<Typography variant="subtitle1">passwords match</Typography>
						</Grid>
					</Grid>
					<Grid></Grid>
				</Grid>
			</Box>
			<Typography variant="caption" align="left">
				{containsThreeCharacterTypes(password)
					? `Password meets character type requirements`
					: `${calculateCharSets(password)} of 3 character types`}
			</Typography>
		</Root>
	);
};
